import React, { useEffect, useState } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Button, FormControl, FormControlLabel, Radio, RadioGroup } from '@mui/material';
import { IconArrowRight, IconDate } from '../../../../icons/Icon';
import styles from './filterdata.module.scss';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import MultiSelectNew from '../../../../components/MultiSelectNew';
import { differenceInDays, format , formatISO, parseISO, set} from 'date-fns';
import { useDispatch } from 'react-redux';
import { AppDispatch, RootState } from '../../../../store/store';
import { fetchDayPartCode, fetchSites, setProductionPlanParams } from '../../../../services/productionPlanReportSlice';
import { fetchRegion } from '../../../../services/regionSlice';
import { useSelector } from 'react-redux';
import enUS from 'date-fns/locale/en-US';
import axiosInstance from '../../../../api/axios';
import { BASE_URL } from '../../../../constants/apiConstants';
import { KitchenIndent } from '../../../../constants/modulConstants';

interface FilterData {
  handleClick: () => void;
  handleMenuChange: (value: boolean) => void;
  handleFilterChange: () => void;
}

interface CutOffDetailsResponse {
  data: {
    cutoffdays: number;
    cutofftimehh: number;
    cutofftimemm: number;
  };
}


const FilterData: React.FC<FilterData> = ({ handleClick, handleMenuChange, handleFilterChange }) => {
  const dispatch = useDispatch<AppDispatch>();
  const roleId = JSON.parse(localStorage.getItem('roleId') || "");
  const { sites, dayparts, previousPlan } = useSelector((state: RootState) => state.productionPlan);
  const { regions } = useSelector((state: RootState) => state.region);
  const siteOptions = sites?.length ? sites?.map((site: any) => { return { id: site?.sitecode, label: site?.sitealias, code: site?.sitecode } }) : [];
  const mealTimeOptions = dayparts?.length ? dayparts?.map((daypart: any) => { return { label: daypart?.name, code: daypart?.daypartcode, ...daypart } }) : [];
  const [userId, setUserId] = useState<number | null>(null);
  const [cutoffTime, setCutoffTime] = useState<any>()
  const [selectDate, setSelectDate] = useState<any>(new Date());
  const [toDate, setToDate] = useState<any>(new Date());
  const [mealTimes, setMealTimes] = useState<string[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [selectedRegionOptions, setSelectedRegionOptions] = useState<string[]>([]);
  const [dataType, setDataType] = useState<string>('fullData'); // Default to Full Data

  const regionOptions = React.useMemo(
    () => (regions || []).map((region: any) => ({
      label: region?.regionname,
      value: String(region?.regionid),
      code: String(region?.regionid),
      id: String(region?.regionid),
    })),
    [regions]
  );

  const getSitesByRegionIds = (regionIds: string[], allSites: any[]) => {
    const hasRegionMapping = allSites.some(
      (site: any) => site?.regionid !== undefined && site?.regionid !== null && site?.regionid !== ''
    );

    if (!regionIds.length || !hasRegionMapping) return allSites;

    const selectedRegionSet = new Set(regionIds.map(String));
    return allSites.filter((site: any) => selectedRegionSet.has(String(site?.regionid)));
  };

  const filteredSites = React.useMemo(() => {
    return getSitesByRegionIds(selectedRegionOptions, sites || []);
  }, [sites, selectedRegionOptions]);

  const visibleSiteOptions = filteredSites?.length
    ? filteredSites.map((site: any) => ({ id: site?.sitecode, label: site?.sitealias, code: site?.sitecode }))
    : [];

  useEffect(() => {
    const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
    if (userInfo) {
      setUserId(userInfo.userdetails.userid);
    }
    GetCutOffdays()
  }, []);

  useEffect(() => {
    if (userId) {
      dispatch(fetchRegion(String(userId)));
      dispatch(fetchSites({ userid: userId })).unwrap()?.then((res) => {
        if (res?.length == 1) {
          const initialOptions = res?.map((site: any) => site?.sitecode);
          setSelectedOptions(initialOptions);
        }
      })?.catch((error) => console.error('fetch Sites production Plan', error));
      dispatch(fetchDayPartCode({ userid: userId, siteidlist: [] })).unwrap()?.then((res) => {
        if (res?.length == 1) {
          const initialOptions = res?.map((daypart: any) => daypart?.daypartcode);
          setMealTimes(initialOptions);
        }
      })?.catch((error) => console.error('fetch daypart production Plan', error));
    }
  }, [userId, dispatch]);

  useEffect(() => {
    if (regionOptions.length === 1 && selectedRegionOptions[0] !== regionOptions[0].value) {
      setSelectedRegionOptions([regionOptions[0].value]);
    }
  }, [regionOptions, selectedRegionOptions]);

  useEffect(() => {
    if (!selectedRegionOptions.length) return;

    const validSiteCodes = new Set(visibleSiteOptions.map((site: any) => site.code));
    setSelectedOptions((prev) => {
      const next = prev.filter((siteCode) => validSiteCodes.has(siteCode));

      if (next.length === 0 && visibleSiteOptions.length === 1) {
        return [visibleSiteOptions[0].code];
      }

      const unchanged = next.length === prev.length && next.every((value, index) => value === prev[index]);
      return unchanged ? prev : next;
    });
  }, [visibleSiteOptions, selectedRegionOptions]);

  const handleChange = (value: any, type: String) => {   
    switch (type) {
      case 'FromDate':
        {
          setSelectDate(value);
           setToDate(value);
        }
        break;
      case 'SiteOption':
        setSelectedOptions(value);
        break;
      case 'RegionOption':
        {
          const nextRegions = value || [];
          setSelectedRegionOptions(nextRegions);

          if (!nextRegions.length) {
            setSelectedOptions([]);
            break;
          }

          const nextAllowedSites = getSitesByRegionIds(nextRegions, sites || []);
          const allowedSiteCodes = new Set(nextAllowedSites.map((site: any) => site?.sitecode));

          setSelectedOptions((prev) => {
            const next = prev.filter((siteCode) => allowedSiteCodes.has(siteCode));

            if (next.length === 0 && nextAllowedSites.length === 1) {
              return [nextAllowedSites[0].sitecode];
            }

            return next;
          });
        }
        break;
      case 'MealOption':
        setMealTimes(value);
        break;
      case 'DataType':
        setDataType(value);
        break;
      case 'ToDate':
        setToDate(value);
        break;

    }
    handleFilterChange();
  };

  // Datepicker settings
  const customEnLocale: Locale = {
    ...enUS,
    options: {
      ...enUS.options,
      weekStartsOn: 1,
    },
  };

  const GetCutOffdays = async () => {
    try {
        const roleId = Number(localStorage.getItem('roleId'));
        const payload = { modulecode: KitchenIndent.code, roleid: roleId, sitecode: "" };
        const dateSettings = await axiosInstance.post<CutOffDetailsResponse>(
            `${BASE_URL}/api/v1/AdminConfiguration/GetCutOffDetails`,
            payload
        );
        setCutoffTime({
          days: dateSettings.data.data.cutoffdays || 1,
          hours: dateSettings.data.data.cutofftimehh || 0,
          minutes: dateSettings.data.data.cutofftimemm || 0,
        });
    } catch (error) {
        console.error("Error fetching time period:", error);
    }
};

  const handleSubmit = () => {
 
  const endtime = formatISO(set(selectDate, {
      hours: cutoffTime?.hours || 24,
      minutes: cutoffTime?.minutes || 0,
      seconds: 0
    }))   
    const disableKI = roleId !== 1 && differenceInDays(parseISO(endtime), new Date()) < cutoffTime?.days;
    const selectedRegionIds = (selectedRegionOptions || []).map(String);
    const payloadSites = selectedRegionIds.length > 0 ? filteredSites : (sites || []);
    const allSiteCodes = payloadSites.map((site: any) => site?.sitecode).filter(Boolean);
    const useAllSites = !selectedOptions.length || selectedOptions.length === allSiteCodes.length;
    const params = {
      fromPlanDate: format(new Date(selectDate), "yyyy-MM-dd'T'00:00:00XXX"),
      toPlanDate: toDate ? format(new Date(toDate), "yyyy-MM-dd'T'00:00:00XXX") : '',
      siteidlist: useAllSites ? allSiteCodes : selectedOptions,
      allsite: useAllSites,
      daypartidlist: mealTimes,
      allmeal: !mealTimes?.length,
      disableKI: disableKI,
      ispartial: dataType === 'partialData', // Send true for Partial Data, false for Full Data
      regionid: selectedRegionIds.length > 0 ? selectedRegionIds : null,
    }
    dispatch(setProductionPlanParams(params));
    handleClick();
    handleMenuChange(false);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={customEnLocale}>
      <FormControl fullWidth>
        <div className="flex justify-content-between align-items-end">
          <div className={`${styles.leftBox} flex justify-content-between align-items-end`}>
            <div className={`date-wrap flex flex-column ${styles.multiSelect}`}>
              <label className={`${styles.label}`}>
                From Date <span>*</span>
              </label>
              <DatePicker
                value={selectDate}
                onChange={(newValue) => handleChange(newValue, "FromDate")}
                // minDate={!previousPlan ? new Date : undefined}
                // maxDate={previousPlan ? new Date().setDate(new Date().getDate() - 1) : undefined}
                slotProps={{ field: { clearable: true } }}
                format='dd MMM yyyy'
                slots={{
                  openPickerIcon: IconDate,
                }}
                label="Select Menu Date"
              />
            </div>
            <div className={`date-wrap flex flex-column ${styles.multiSelect}`}>
              <label className={`${styles.label}`}>
                To Date <span>*</span>
              </label>
              <DatePicker
                value={toDate}
                onChange={(newValue) => handleChange(newValue, "ToDate")}
                // minDate={!previousPlan ? new Date : undefined}
                disabled={!selectDate}
                minDate={selectDate}
                // maxDate={previousPlan ? new Date().setDate(new Date().getDate() - 1) : undefined}
                slotProps={{ field: { clearable: true } }}
                format='dd MMM yyyy'
                slots={{
                  openPickerIcon: IconDate,
                }}
                label="Select To Date"
              />
            </div>
            <div className={`flex flex-column mr-24 ${styles.multiSelect}`}>
              <MultiSelectNew
                label="Region"
                options={regionOptions}
                selectedValues={selectedRegionOptions}
                onApply={(selected) => handleChange(selected, "RegionOption")}
                placeholder="Select Region"
                showLabelOnly={true}
                selectAllLabel='All Region'
                hideSelectAllOnSearch={true}
                disabled={regionOptions.length === 1}
              />
            </div>
            <div className={`flex flex-column mr-24 ${styles.multiSelect}`}>
              <MultiSelectNew
                options={visibleSiteOptions}
                label='Select Site'
                disabled={visibleSiteOptions?.length == 1}
                selectedValues={selectedOptions}
                onApply={(e) => handleChange(e, "SiteOption")}
                placeholder='Select Site'
                selectAllLabel='All Site'
                hideSelectAllOnSearch={true}
              />
            </div>
            <div className={`flex flex-column ${styles.multiSelect}`}>
              <MultiSelectNew
                options={mealTimeOptions}
                label='Select Mealtime'
                disabled={siteOptions?.length == 0 || mealTimeOptions?.length == 1}
                selectedValues={mealTimes}
                onApply={(e) => handleChange(e, "MealOption")}
                showLabelOnly={true}
                placeholder='Select Mealtime'
                selectAllLabel='All Meals'
              />
            </div>
            <div  className={`flex flex-column ${styles.dataTypeWrap} d-none`}>
              <label className={`${styles.label}`}>
                Incremental Production 
              </label>
              <RadioGroup
                aria-labelledby='data-type-radio-buttons-group-label'
                name='data-type-radio-buttons-group'
                className={`${styles.row}`}
                row
                value={dataType}
                onChange={(e) => handleChange(e.target.value, "DataType")}
              >
                <FormControlLabel
                  value='fullData'
                  control={<Radio />}
                  label='Full Data'
                  className={`${styles.col}`}
                />
                <FormControlLabel
                  value='partialData'
                  control={<Radio />}
                  label='Partial Data'
                  className={`${styles.col}`}
                />
              </RadioGroup>
            </div>
          </div>
          <Button onClick={handleSubmit} className={`primary-btn ${styles.primaryBtn}`} disabled={!(selectDate && toDate)}>
            Go <IconArrowRight />
          </Button>
        </div>
      </FormControl>
    </LocalizationProvider>
  );
};

export default FilterData;
